import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from "clsx";
import React, { useState, useRef, useEffect, } from "react";
import { CaretDownIcon, SearchIcon, AlertIcon } from "../Icons/Icons";
import { getOptionKey } from "../../utils/utils";
import { useLocalizedString } from "@amzn/aws-marketing-identity-kit-localizations";
const SearchDropdown = React.forwardRef(({ label, name, searchLabel, items, onSelectionChange, isInvalid, isDisabled, errorMessage, selectedKey = "", showCurrentSelected = true, selectLabel, readOnly, className, value, onChange, ...props }, forwardedRef) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const { t } = useLocalizedString();
    const [selectedOption, setSelectedOption] = useState(items.find((item) => item?.value === value || item?.value === selectedKey));
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [activeDescendant, setActiveDescendant] = useState("");
    const buttonRef = useRef(null);
    const searchInputRef = useRef(null);
    const listRef = useRef(null);
    const dropdownRef = useRef(null);
    const optionRefs = useRef([]);
    const filteredItems = items.filter((option) => t(option.key).toLowerCase().includes(searchTerm.toLowerCase()));
    useEffect(() => {
        if (value) {
            const option = items.find((item) => item.value === value);
            if (option) {
                setSelectedOption(option);
            }
        }
    }, [value, items]);
    useEffect(() => {
        if (!buttonRef.current)
            return;
        if (typeof forwardedRef === "function") {
            forwardedRef(buttonRef.current);
        }
        else if (forwardedRef) {
            forwardedRef.current = buttonRef.current;
        }
    }, [forwardedRef]);
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);
    const handleMainInputClick = (e) => {
        e.preventDefault();
        if (isDisabled)
            return;
        setIsOpen((prev) => !prev);
        setSearchTerm("");
    };
    const handleSearchInputChange = (e) => {
        setSearchTerm(t(e.target.value));
        setFocusedIndex(-1);
        setActiveDescendant("");
    };
    const handleOptionClick = (option) => {
        setSelectedOption(option);
        onChange?.(option.value);
        onSelectionChange?.(option.value);
        setIsOpen(false);
        setSearchTerm("");
        setActiveDescendant("");
        if (forwardedRef && "current" in forwardedRef && forwardedRef.current) {
            forwardedRef.current.focus();
        }
    };
    const handleKeyDown = (e) => {
        switch (e.key) {
            case "Enter":
                e.preventDefault();
                if (isDisabled)
                    return;
                if (!isOpen) {
                    setIsOpen(true);
                    setSearchTerm("");
                }
                else if (focusedIndex >= 0) {
                    handleOptionClick(filteredItems[focusedIndex]);
                }
                break;
            case "ArrowDown":
                e.preventDefault();
                if (isDisabled)
                    return;
                if (!isOpen) {
                    setIsOpen(true);
                    setSearchTerm("");
                }
                else {
                    const newIndex = Math.min(focusedIndex + 1, filteredItems.length - 1);
                    setFocusedIndex(newIndex);
                    setActiveDescendant(`option-${filteredItems[newIndex].value}`);
                }
                break;
            case "ArrowUp":
                e.preventDefault();
                if (isOpen) {
                    const newIndex = Math.max(focusedIndex - 1, -1);
                    setFocusedIndex(newIndex);
                    setActiveDescendant(newIndex >= 0 ? `option-${filteredItems[newIndex].value}` : "");
                }
                break;
            case "Escape":
                setIsOpen(false);
                setFocusedIndex(-1);
                setSearchTerm("");
                setActiveDescendant("");
                if (forwardedRef &&
                    "current" in forwardedRef &&
                    forwardedRef.current) {
                    forwardedRef.current.focus();
                }
                break;
            case "Tab":
                if (isOpen) {
                    e.preventDefault();
                    if (e.shiftKey) {
                        if (focusedIndex > 0) {
                            setFocusedIndex(focusedIndex - 1);
                            setActiveDescendant(`option-${filteredItems[focusedIndex - 1].value}`);
                        }
                        else {
                            setFocusedIndex(-1);
                            setActiveDescendant("");
                            searchInputRef.current?.focus();
                        }
                    }
                    else {
                        if (focusedIndex === -1) {
                            setFocusedIndex(0);
                            setActiveDescendant(`option-${filteredItems[0].value}`);
                        }
                        else if (focusedIndex < filteredItems.length - 1) {
                            setFocusedIndex(focusedIndex + 1);
                            setActiveDescendant(`option-${filteredItems[focusedIndex + 1].value}`);
                        }
                        else {
                            setIsOpen(false);
                            setFocusedIndex(-1);
                            setActiveDescendant("");
                        }
                    }
                }
                break;
        }
    };
    useEffect(() => {
        if (focusedIndex >= 0 && focusedIndex < optionRefs.current.length) {
            optionRefs.current[focusedIndex]?.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    }, [focusedIndex]);
    useEffect(() => {
        const handleClick = (event) => {
            if (buttonRef.current?.contains(event.target)) {
                return;
            }
            if (dropdownRef.current &&
                !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setActiveDescendant("");
                setSearchTerm("");
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);
    if (readOnly) {
        return (_jsxs("div", { className: clsx("flex flex-col items-start gap-1 border-b border-[#ccccd1] px-0 py-4 sm:p-4 h-auto min-h-[8.4rem] overflow-hidden", "last:border-b-0 nth-last-child(2):border-b-0", className), children: [_jsx("div", { className: "text-sm text-idk-disabled-text", children: label }), _jsx("div", { className: "text-sm", children: t(getOptionKey(items, selectedOption?.value || "")) })] }));
    }
    return (_jsx("div", { ...props, onKeyDown: handleKeyDown, role: "combobox", tabIndex: 0, "aria-expanded": isOpen, "aria-haspopup": "listbox", "aria-controls": "items-list", "aria-owns": "items-list", "aria-autocomplete": "list", "aria-activedescendant": activeDescendant, className: "relative w-full focus:outline-none", children: _jsxs("div", { children: [_jsxs("div", { className: `relative w-full border rounded-lg text-left h-[52px] ${isInvalid && errorMessage ? "border-red-600" : "border-gray-300"} ${isDisabled ? "bg-gray-100" : ""}`, children: [_jsx("div", { className: "absolute top-[3px] left-3 text-sm text-gray-600 pointer-events-none", children: label }), _jsxs("button", { ref: buttonRef, value: selectedOption ? selectedOption.key : "", onClick: handleMainInputClick, "aria-controls": "items-list", className: `
    relative flex justify-between items-end w-full h-full px-3 text-base bg-transparent 
    border-0 rounded-lg cursor-pointer outline-none focus:ring-1 focus:ring-blue-500 focus:shadow-idk-box-shadow
    ${isDisabled ? "opacity-60 pointer-events-none bg-gray-100" : ""}
    ${isOpen ? "shadow-idk-box-shadow" : ""}
    ${!isOpen && !isDisabled ? "hover:shadow-idk-box-shadow" : ""}
    transition-all duration-300 ease-in-out
  `, "aria-label": name, children: [_jsx("span", { className: "mt-4 overflow-hidden overflow-ellipsis whitespace-nowrap", children: t(selectedOption?.key && showCurrentSelected
                                        ? selectedOption.key
                                        : selectLabel || label) }), _jsx("div", { className: "absolute right-3 top-1/2 -translate-y-1/2", children: _jsx("span", { className: clsx("transform transition-transform duration-200 block", isOpen ? "-rotate-180" : ""), "aria-hidden": "true", children: _jsx(CaretDownIcon, {}) }) })] }), isOpen && (_jsxs("div", { ref: dropdownRef, className: "absolute left-0 right-0 z-[2000] flex flex-col max-h-[calc(100vh-60px)] \n                          overflow-y-hidden bg-white border border-gray-300 rounded-lg shadow-lg \n                          top-[57px] p-2", children: [_jsxs("div", { className: "relative", children: [_jsx("span", { className: "absolute left-3 top-3 text-gray-400", children: _jsx(SearchIcon, {}) }), _jsx("input", { ref: searchInputRef, type: "text", value: searchTerm, onChange: handleSearchInputChange, placeholder: searchLabel, className: "w-full px-10 py-2.5 text-base border-b border-gray-300 outline-none focus:ring-2 focus:ring-blue-500", name: name })] }), _jsx("ul", { id: "items-list", ref: listRef, role: "listbox", className: "max-h-[200px] overflow-y-auto list-none p-0 pt-2 m-0", children: filteredItems.map((option, index) => (_jsx("li", { id: `option-${option.value}`, role: "option", tabIndex: -1, "aria-selected": option === selectedOption, onClick: () => handleOptionClick(option), onMouseEnter: () => {
                                            setFocusedIndex(index);
                                            setActiveDescendant(`option-${option.value}`);
                                        }, ref: (el) => (optionRefs.current[index] = el), className: `
                        rounded-lg px-3 py-3 text-base font-medium flex justify-between cursor-pointer
                        outline-none focus:ring-2 focus:ring-blue-500
                        ${option === selectedOption || index === focusedIndex
                                            ? "bg-idk-gradient"
                                            : ""}
                      `, children: t(option.key) }, option.value))) })] }))] }), isInvalid && errorMessage && (_jsxs("div", { className: "flex items-center gap-1 mt-2.5 text-red-600 text-sm font-medium", children: [_jsx(AlertIcon, {}), _jsx("span", { children: errorMessage })] }))] }) }));
});
SearchDropdown.displayName = "SearchDropdown";
export default SearchDropdown;
//# sourceMappingURL=SearchDropdown.js.map